import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { toastBuildError } from '@/components/profiles/profile-wizard';
import { PageContent } from '@/components/page-content';
import { SectionHeader } from '@/components/section-header';
import { RequireOrgEdit } from '@/components/require-permission';
import { companyProfilesOptions } from '@/hooks/query-options';
import {
  CompanyProfilesService,
  type CompanyProfile,
} from '@/api/company-profiles-service';
import {
  CompanyProfileBuildingState,
  CompanyProfileEmptyState,
  type ProfileBuildSources,
} from '@/components/profiles/company-profile-empty-state';
import {
  TargetAccountCard,
  YourCompanyProfileCard,
} from '@/components/profiles/company-profile-cards';

export const Route = createFileRoute(
  '/_auth/_side-layout/actions/profiles/profiles',
)({
  component: () => (
    <PageContent>
      <ProfilesList />
    </PageContent>
  ),
});

// "Our Profiles" (Concord GTM Screens 4d populated): the org's own profile as
// the hero, with the Target Accounts grid below. The first build runs INLINE
// (the empty-state card becomes the running state).
function ProfilesList() {
  const { data, isLoading } = useQuery(companyProfilesOptions());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // The website being researched by the inline first build; null = not showing.
  const [buildingSite, setBuildingSite] = useState<string | null>(null);

  const profiles = data?.profiles ?? [];
  // "Our Profiles" is the org's own GTM profiles (kind 'self'); Target Accounts
  // (kind 'account') live in their own tab. The DEFAULT self profile is the
  // hero (it's the org_profile agents run on); the other self profiles render
  // in the grid below.
  const selfProfiles = profiles.filter((p) => p.kind === 'self');
  const heroProfile = selfProfiles.find((p) => p.is_default) ?? selfProfiles[0];
  const otherProfiles = selfProfiles.filter((p) => p.id !== heroProfile?.id);

  // First build, run inline from the empty state's URL field. The Profile
  // Agent + ICP Analyst work together on it; on success the profile appears
  // via invalidation and the running state unmounts naturally.
  const build = useMutation({
    mutationFn: (input: { website: string; sources?: ProfileBuildSources }) =>
      CompanyProfilesService.build({
        website: input.website,
        ...input.sources,
        mode: 'create',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['company-profiles'] });
      toast.success('Your Company Profile is ready');
    },
    onError: toastBuildError,
    onSettled: () => setBuildingSite(null),
  });

  const startBuild = (website: string, sources?: ProfileBuildSources) => {
    setBuildingSite(website);
    build.mutate({ website, sources });
  };
  // The backend run can't be aborted — Cancel returns to the form and lets the
  // run finish quietly in the background (invalidation still lands the result).
  const cancelBuild = () => {
    setBuildingSite(null);
    toast.info('The build continues in the background.');
  };

  // Clicking a card opens the profile's full-page view/edit route.
  const openProfile = (profile: CompanyProfile) =>
    navigate({
      to: '/actions/profiles/$profileId',
      params: { profileId: profile.id },
    });

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Your Company (GTM)"
        subTitle="The profile every agent works from."
      />

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : heroProfile ? (
        <YourCompanyProfileCard profile={heroProfile} onView={openProfile} />
      ) : build.isPending && buildingSite ? (
        <CompanyProfileBuildingState
          website={buildingSite}
          onCancel={cancelBuild}
        />
      ) : (
        <RequireOrgEdit
          fallback={
            <p className="text-muted-foreground text-sm">
              No company profile yet.
            </p>
          }
        >
          <CompanyProfileEmptyState onBuild={startBuild} />
        </RequireOrgEdit>
      )}

      {otherProfiles.length > 0 && (
        <div className="space-y-4">
          <SectionHeader
            title="Other Profiles"
            subTitle="Agents use your default profile unless an action pins one. Open a profile to make it the default."
          />
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {otherProfiles.map((profile) => (
              <TargetAccountCard
                key={profile.id}
                profile={profile}
                onView={openProfile}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
